import type { ReasoningEvidence, ReasoningRecord, ReasoningSession } from "~/api/reasoningTypes";

/**
 * The shared reasoning fixture: one session with a pinned context, an alternative and an answer.
 * `fakeReasoning.ts` seeds from it and slate's component tests read it, so a shape change here is
 * seen by both at once.
 */
export const FIXTURE_ACTOR_ID = "00000000-0000-4000-8000-00000000a0a0";

export const evidence: ReasoningEvidence = {
  sourceKind: "canopy",
  sourceId: "source-1",
  revision: "source-r1",
  contentDigest: "a".repeat(64),
  observedEpoch: 0,
};
export const record = (
  id: string,
  kind: string,
  payload: Record<string, unknown>,
  extra: Partial<ReasoningRecord> = {},
): ReasoningRecord => ({
  id,
  kind,
  logicalId: id,
  revision: 1,
  schemaVersion: "reasoning-engine/1",
  payload,
  payloadDigest: "digest",
  actorId: FIXTURE_ACTOR_ID,
  createdAt: "2026-10-07T09:00:00Z",
  eligible: true,
  ineligibilityReason: null,
  ...extra,
});
export function reasoningSession(): ReasoningSession {
  return {
    id: "session-1",
    runId: "run-1",
    workItemId: null,
    schemaVersion: "reasoning-engine/1",
    strategyVersion: "sequential/1",
    revision: 3,
    dependencyEpoch: 0,
    state: "reasoning",
    criteriaDigest: "criteria",
    criteria: [],
    contextRecordId: "context-1",
    currentAcceptanceRecordId: null,
    records: [
      record("context-1", "context", { evidence: [evidence] }),
      record(
        "parent-1",
        "candidate",
        {
          content: "Alternative",
          evidence: [evidence],
          contextRecordId: "context-1",
          derivedFromRecordIds: [],
          evidenceComplete: true,
        },
        { logicalId: "alternative" },
      ),
      record(
        "candidate-1",
        "candidate",
        {
          content: "Original answer",
          rationale: "Observed evidence",
          evidence: [evidence],
          contextRecordId: "context-1",
          derivedFromRecordIds: [],
          evidenceComplete: true,
        },
        { logicalId: "answer" },
      ),
    ],
  };
}
export function revisedSession(): ReasoningSession {
  const session = reasoningSession();
  return {
    ...session,
    revision: 4,
    records: [
      ...session.records,
      record(
        "candidate-2",
        "candidate",
        {
          content: "Concurrent answer",
          rationale: "Another review",
          evidence: [evidence],
          contextRecordId: "context-1",
          derivedFromRecordIds: ["candidate-1"],
          evidenceComplete: true,
        },
        { logicalId: "answer", revision: 2 },
      ),
    ],
  };
}
